// CO-10 — how much of an athlete's logged work carries a recorded effort
// value (RIR/RPE). The coach panel's question is "can I trust this
// athlete's effort-driven math?", because the whole target/hard-set layer
// (AP-2 onward) is only as good as its RIR coverage.
//
// Two decisions are baked in here, both deliberate:
//
// 1. DENOMINATOR = counted sets, warm-ups INCLUDED. The logged model has
//    no warm-up concept (LogSetKind is a load-type axis: weighted /
//    bodyweight / p1rm / loaded-carry / cardio / timed — no intent axis),
//    and programs don't model warm-ups either (AP-7). Rather than invent a
//    load-relative heuristic — which would be a second, divergent notion of
//    "working set" and would misfire on constant-load work — we reuse the
//    ONE existing predicate (isCountedLogSet) that already decides what
//    ExerciseResult.setCount/hardSetCount count. So this ratio can never
//    disagree with the hard-set panel next to it. The cost: an athlete who
//    tags every working set but ramps up to it reads below 100%. Accepted
//    while effort tagging is sparse enough that the live question is "does
//    this athlete tag at all?" (Antenore 2026-07-14); when density makes
//    60%-vs-85% meaningful, the fix is to give the MODEL a warm-up concept,
//    not to guess here.
//
//    Two duration kinds sit on opposite sides of that predicate, and MX-5
//    slice 6 checked both. A TIMED HOLD is IN: its time under tension is the
//    work it produced, so a plank lands in the ratio like any other set. A
//    CARDIO BOUT is OUT (Antenore, 2026-07-22: «lascia il cardio fuori») —
//    their cardio comes from the watch via Health Connect, which cannot know
//    an RPE, so counting it would measure the DEVICE's silence as the
//    athlete's habit. Same failure mode as decision 2 below, one axis over.
//    The exclusion lives in setMetrics (cardio gets no TUT); see the note
//    there before "fixing" it, and the two pinning tests in
//    effortCoverage.test.ts.
//
// 2. WINDOW STARTS AT THE ATHLETE'S OWN EFFORT ERA, derived from data.
//    Effort input landed with the RIR Logger in v2.38.0 (2026-05-26); before
//    that no set could carry rir/rpe at all, so counting that stretch would
//    drag the ratio down for a reason that belongs to the app, not to the
//    athlete. (v3.87.3 / AP-0b, 2026-07-06, only made the meter the set-
//    COMPLETION gesture. It made tagging near-mandatory; it did not make it
//    possible. Don't conflate the two — I did, see below.)
//
//    Deriving beats hardcoding, and the family's own data proved it on the
//    day this shipped: every era is per-athlete and none is a release date.
//    Leonardo's starts 2026-05-31 (he used the RIR column as soon as it
//    existed), Erika's 2026-07-13 (she just started). A hardcoded 2026-07-06
//    — which is what the author of this file wrongly believed the era to be
//    — would have silently discarded five weeks of Leonardo's real tagging.
//    Devices also update via Obtainium at different times, a backfill or an
//    import can land effort on older rows, and a user who joins later has
//    effort from THEIR day one. No constant survives all of that.
import { effectiveRir } from "./effortScale.js";
import type { Exercise } from "./schemas/exercise.js";
import type { LogSet } from "./schemas/logSet.js";
import { isCountedLogSet } from "./setMetrics.js";
import { catalogBySlug } from "./volumeByMuscle.js";

// isCountedLogSet needs a POSITIVE bodyweight, not a true one — a
// bodyweight set's volume scales linearly with it, so the counted boolean
// is invariant to its magnitude. The coach read path can't see the
// athlete's bodyweight (pickCoachProfileFields withholds it by design), and
// with this it doesn't need to.
const NOMINAL_BODYWEIGHT_KG = 1;

export type EffortCoverage = {
  // Counted sets performed at/after eraStart — the denominator.
  counted: number;
  // Of those, the ones carrying a recorded effort (effectiveRir != null,
  // so a fail counts as tagged: it IS the maximal-effort signal).
  tagged: number;
  // tagged/counted as a 0..100 percentage, or null when there is nothing
  // to divide (no tagged set at all → the panel says "no coverage", never
  // a false "0%").
  pct: number | null;
  // performedAt of the earliest effort-tagged set in the input, or null when
  // there is none. The panel names this date so the coach reads a gap as
  // "the app didn't ask yet", not "the athlete is careless".
  eraStart: string | null;
  // Counted sets that fall BEFORE eraStart and were therefore left out.
  // This is what tells eraStart apart from a window artefact: >0 means the
  // input actually straddles the athlete's first tagged set, so eraStart is
  // a real boundary worth naming. 0 means the window simply starts inside
  // the effort era (or there is no era) and the date would be noise.
  preEraCounted: number;
};

// `sets` is any window of the athlete's raw LogSets (order irrelevant).
// `catalog` is the exercise list, taken and resolved the same way its
// closest sibling programWeeklyPrescription takes it.
export const effortCoverage = (
  sets: readonly LogSet[],
  catalog: ReadonlyArray<Exercise>,
): EffortCoverage => {
  const bySlug = catalogBySlug(catalog);
  let eraStart: string | null = null;
  for (const set of sets) {
    if (effectiveRir(set) == null) continue;
    if (eraStart === null || set.performedAt < eraStart) {
      eraStart = set.performedAt;
    }
  }
  if (eraStart === null) {
    return {
      counted: 0,
      tagged: 0,
      pct: null,
      eraStart: null,
      preEraCounted: 0,
    };
  }

  let counted = 0;
  let tagged = 0;
  let preEraCounted = 0;
  for (const set of sets) {
    if (
      !isCountedLogSet(set, bySlug.get(set.exerciseSlug), NOMINAL_BODYWEIGHT_KG)
    ) {
      continue;
    }
    if (set.performedAt < eraStart) {
      preEraCounted += 1;
      continue;
    }
    counted += 1;
    if (effectiveRir(set) != null) tagged += 1;
  }

  return {
    counted,
    tagged,
    pct: counted === 0 ? null : Math.round((tagged / counted) * 100),
    eraStart,
    preEraCounted,
  };
};
